import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ActionFilter from '../../components/ActionFilter';
import CommonPagination from '../../components/Table/CommonPagination';
import PatchUpdateForm from './PatchUpdateForm';
import AxiosInstance from '../../services/networkServices';
import PageLayout from '../../components/PageLayout';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 'calc(100vh - 205px)',
    maxHeight: 'calc(100vh - 205px)',
    boxShadow: 'none !important',
  },
  tableCell: {
    fontSize: {
      xs: '14px !important',
      sm: '15px !important',
      md: '15px !important',
      lg: '16px !important',
      xl: '16px !important',
    },
    color: '#000 !important',
    fontWeight: '600 !important',
    padding: {
      xs: '4px 5px !important',
      sm: '6px 8px !important',
      md: '6px 8px !important',
      lg: '8px 10px !important',
      xl: '8px 10px !important',
    },
    backgroundColor: '#e0e0e0',
  },
  tableBody: {
    fontSize: {
      xs: '12px !important',
      sm: '13px !important',
      md: '14px !important',
      lg: '15px !important',
      xl: '15px !important',
    },
    color: '#333 !important',
    padding: {
      xs: '10px 5px !important',
      sm: '10px !important',
      md: '10px !important',
      lg: '10px 15px !important',
      xl: '10px 15px !important',
    },
  },
  table: {
    border: '1px solid rgba(224, 224, 224, 1)',
    minWidth: '100%',
    minHeight: '100%',
  },
  stickyHeader: {
    position: 'sticky',
    top: '-1px',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
}));

const STATUS_CHIP_COLORS = {
  PENDING: 'warning',
  SENT: 'success',
  FAILED: 'error',
  CANCELED: 'default',
};

/** e.g. "3 text · 2 images" */
const summarizeBlocks = (blocks = []) => {
  const textCount = blocks.filter((block) => block.type === 'TEXT').length;
  const imageCount = blocks.filter((block) => block.type === 'IMAGE').length;
  const parts = [];
  if (textCount) parts.push(`${textCount} text`);
  if (imageCount) parts.push(`${imageCount} image${imageCount === 1 ? '' : 's'}`);
  return parts.join(' · ') || '—';
};

const ManagePatchUpdates = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [patchUpdates, setPatchUpdates] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedPatchUpdate, setSelectedPatchUpdate] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patchUpdateToDelete, setPatchUpdateToDelete] = useState(null);

  useEffect(() => {
    fetchPatchUpdates();
  }, [page]);

  const fetchPatchUpdates = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get('/admin/patch-updates');
      setPatchUpdates(response.data.data || []);
      setTotalCount(response.data.data?.length || 0);
    } catch (error) {
      console.error('Error fetching patch updates:', error);
      toast.error('Failed to load patch updates');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleAddPatchUpdate = () => {
    setSelectedPatchUpdate(null);
    setFormOpen(true);
  };

  const handleEditPatchUpdate = (patchUpdate) => {
    setSelectedPatchUpdate(patchUpdate);
    setFormOpen(true);
  };

  const handleDeletePatchUpdate = (patchUpdate) => {
    setPatchUpdateToDelete(patchUpdate);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await AxiosInstance.delete(
        `/admin/patch-update/${patchUpdateToDelete._id}`
      );
      if (response.status === 200) {
        toast.success('Patch update deleted');
        fetchPatchUpdates();
        setDeleteDialogOpen(false);
        setPatchUpdateToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting patch update:', error);
      toast.error('Failed to delete patch update');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPatchUpdateToDelete(null);
  };

  const handleFormSubmit = async (payload) => {
    try {
      if (selectedPatchUpdate) {
        await AxiosInstance.put(
          `/admin/patch-update/${selectedPatchUpdate._id}`,
          payload
        );
        toast.success('Patch update saved');
      } else {
        await AxiosInstance.post('/admin/patch-update', payload);
        toast.success(
          payload.sendDate ? 'Patch update scheduled' : 'Patch update published'
        );
      }
      fetchPatchUpdates();
      return true;
    } catch (error) {
      console.error('Error saving patch update:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to save patch update'
      );
      return false;
    }
  };

  const handleCloseDialog = () => {
    setSelectedPatchUpdate(null);
    setFormOpen(false);
  };

  return (
    <PageLayout
      title="Patch Updates"
      loading={loading}
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddPatchUpdate}
          startIcon={<AddIcon />}
        >
          Add Patch Update
        </Button>
      }
    >
      <Box className={classes.root}>
        <Box
          sx={{
            padding: '15px 15px 0 15px',
            borderRadius: '6px',
            boxShadow:
              'rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px',
          }}
        >
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell className={classes.tableCell}>Name</TableCell>
                  <TableCell className={classes.tableCell}>Build</TableCell>
                  <TableCell className={classes.tableCell}>Content</TableCell>
                  <TableCell className={classes.tableCell}>Send Date</TableCell>
                  <TableCell className={classes.tableCell}>Push</TableCell>
                  <TableCell className={classes.tableCell}>Status</TableCell>
                  <TableCell className={classes.tableCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patchUpdates.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className={classes.tableBody}
                      align="center"
                    >
                      No patch updates found
                    </TableCell>
                  </TableRow>
                ) : (
                  patchUpdates.map((patchUpdate) => (
                    <TableRow key={patchUpdate._id}>
                      <TableCell className={classes.tableBody}>
                        {patchUpdate.name}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {patchUpdate.buildName}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {summarizeBlocks(patchUpdate.blocks)}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {dayjs(patchUpdate.sendDate).format(
                          'ddd, MMM D, YYYY @ hh:mm A'
                        )}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {patchUpdate.sendPush ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        <Chip
                          label={patchUpdate.status || 'PENDING'}
                          color={
                            STATUS_CHIP_COLORS[patchUpdate.status] || 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        <ActionFilter
                          onEdit={() => handleEditPatchUpdate(patchUpdate)}
                          onDelete={() => handleDeletePatchUpdate(patchUpdate)}
                          index={patchUpdate._id}
                          notification
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <CommonPagination
            count={totalCount}
            rowsPerPage={10}
            page={page}
            onPageChange={handlePageChange}
          />
        </Box>

        {/* Delete Patch Update Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Patch Update</DialogTitle>
          <DialogContent>
            <Typography color="error">
              {patchUpdateToDelete?.status === 'SENT'
                ? 'This patch update has already been published — deleting it will remove it from users\' notification feeds.'
                : 'Are you sure you want to delete this patch update? Any scheduled publish will be canceled.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Patch Update Dialog */}
        <PatchUpdateForm
          open={formOpen}
          onClose={handleCloseDialog}
          onSubmit={handleFormSubmit}
          initialData={selectedPatchUpdate}
        />
      </Box>
      <ToastContainer position="top-right" autoClose={3000} />
    </PageLayout>
  );
};

export default ManagePatchUpdates;
